import Refresh from "@mui/icons-material/Refresh";
import {
  Box,
  IconButton,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import api from "app/api";
import { DatePicker, PageHeader, Toolbar } from "common";
import { globalMessageHandler } from "common/components/GlobalMessaging";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import I18n from "i18n";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";

interface DailyTripProgram {
  program_id: number;
  program_name: string;
  program_color?: string;
  max_places: number | null;
  guests: number;
  bookings: number;
}

interface DailyTripItem {
  event_group_id: number;
  start_time: string;
  boat_name: string;
  boat_color?: string;
  number_of_places: number;
  total_guests: number;
  total_bookings: number;
  programs: DailyTripProgram[];
}

interface DailyTripsFilter {
  date: string;
  boatIds?: number[];
  programIds?: number[];
}

function ColorDot({ color }: { color?: string }) {
  if (!color) return null;
  return (
    <Box
      component="span"
      sx={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "50%",
        backgroundColor: color,
        flexShrink: 0,
      }}
    />
  );
}

export default function DailyTripsReport() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<DailyTripItem[]>([]);
  const [filter, setFilter] = useState<DailyTripsFilter>({
    date: moment().format("YYYY-MM-DD"),
    boatIds: [],
    programIds: [],
  });

  const requestParams = useMemo(
    () => ({
      date: filter.date,
      boatIds: filter.boatIds,
      programIds: filter.programIds,
    }),
    [filter],
  );

  async function load() {
    setLoading(true);
    const { success, data, error } = await api.get("reports/daily-trips", {
      params: requestParams,
    });
    setLoading(false);
    if (success) {
      setRows(data);
    } else {
      globalMessageHandler.alert({
        severity: "error",
        message: typeof error === "string" ? error : JSON.stringify(error),
      });
    }
  }

  useEffect(() => {
    load();
  }, [requestParams]);

  const sorted = useMemo(
    () =>
      [...rows].sort((a, b) => moment(a.start_time).diff(moment(b.start_time))),
    [rows],
  );

  return (
    <Box>
      <PageHeader title={I18n.t("DailyTripsReport.title")} />
      {loading && <LinearProgress />}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Toolbar sx={{ gap: 2, alignItems: "flex-start" }}>
          <DatePicker
            label="Dátum"
            value={filter.date}
            onChange={evt =>
              setFilter({
                ...filter,
                date: evt.target.value ?? moment().format("YYYY-MM-DD"),
              })
            }
          />
          <IconButton onClick={load} title="Frissítés">
            <Refresh />
          </IconButton>
        </Toolbar>
        <Box sx={{ mt: 2 }}>
          <BoatCheckboxSelector
            color={theme.palette.primary.main}
            value={filter.boatIds || []}
            onChange={evt =>
              setFilter({
                ...filter,
                boatIds: (evt.target?.value ? evt.target?.value : []) as any,
              })
            }
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <ProgramCheckboxSelector
            includeInactive
            value={filter.programIds || []}
            onChange={evt =>
              setFilter({
                ...filter,
                programIds: (evt.target?.value ? evt.target?.value : []) as any,
              })
            }
          />
        </Box>
      </Paper>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          //maxHeight: `calc(100vh - ${headerHeight}px - 220px)`,
          ml: isMobile ? -3 : 0,
          mr: isMobile ? -3 : 0,
          borderRadius: isMobile ? 0 : undefined,
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>Időpont</TableCell>
              <TableCell>Hajó</TableCell>
              <TableCell align="right">Limit</TableCell>
              <TableCell align="right">Össz. vendég</TableCell>
              <TableCell align="right">Össz. fog.</TableCell>
              <TableCell>Program</TableCell>
              <TableCell align="right">Limit</TableCell>
              <TableCell align="right">Vendégek</TableCell>
              <TableCell align="right">Foglalások</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ py: 2 }}
                  >
                    Nincs adat
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {sorted.map(row => {
              const utilizationPct =
                row.number_of_places > 0
                  ? Math.round((row.total_guests / row.number_of_places) * 100)
                  : null;
              const overLimit = row.total_guests > row.number_of_places;
              const programCount = Math.max(row.programs.length, 1);

              return row.programs.length === 0 ? (
                <TableRow key={row.event_group_id} hover>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {moment(row.start_time).format("LT")}
                  </TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.75 }}
                    >
                      <ColorDot color={row.boat_color} />
                      {row.boat_name}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{row.number_of_places}</TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      color: overLimit ? theme.palette.error.main : undefined,
                      fontWeight: overLimit ? 700 : undefined,
                    }}
                  >
                    {row.total_guests}
                    {utilizationPct !== null && (
                      <Typography
                        variant="caption"
                        sx={{ ml: 0.5, color: "text.secondary" }}
                      >
                        ({utilizationPct}%)
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{row.total_bookings}</TableCell>
                  <TableCell colSpan={4} />
                </TableRow>
              ) : (
                row.programs.map((p, idx) => (
                  <TableRow
                    key={`${row.event_group_id}-${p.program_id}`}
                    hover={idx === 0}
                  >
                    {idx === 0 && (
                      <>
                        <TableCell
                          rowSpan={programCount}
                          sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}
                        >
                          {moment(row.start_time).format("LT")}
                        </TableCell>
                        <TableCell
                          rowSpan={programCount}
                          sx={{ verticalAlign: "top" }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.75,
                            }}
                          >
                            <ColorDot color={row.boat_color} />
                            {row.boat_name}
                          </Box>
                        </TableCell>
                        <TableCell
                          rowSpan={programCount}
                          align="right"
                          sx={{ verticalAlign: "top" }}
                        >
                          {row.number_of_places}
                        </TableCell>
                        <TableCell
                          rowSpan={programCount}
                          align="right"
                          sx={{
                            verticalAlign: "top",
                            color: overLimit
                              ? theme.palette.error.main
                              : undefined,
                            fontWeight: overLimit ? 700 : undefined,
                          }}
                        >
                          {row.total_guests}
                          {utilizationPct !== null && (
                            <Typography
                              variant="caption"
                              sx={{ ml: 0.5, color: "text.secondary" }}
                            >
                              ({utilizationPct}%)
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell
                          rowSpan={programCount}
                          align="right"
                          sx={{ verticalAlign: "top" }}
                        >
                          {row.total_bookings}
                        </TableCell>
                      </>
                    )}
                    <TableCell>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.75,
                        }}
                      >
                        <ColorDot color={p.program_color} />
                        {p.program_name}
                      </Box>
                    </TableCell>
                    <TableCell align="right">{p.max_places ?? "–"}</TableCell>
                    <TableCell align="right">{p.guests}</TableCell>
                    <TableCell align="right">{p.bookings}</TableCell>
                  </TableRow>
                ))
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
